import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'
import { Form, Formik } from 'formik'

import { HttpStatus } from 'enums'
import { Portfolio } from 'types'

import { paveApi } from 'api'
import ModalLoader from 'components/common/Loader/ModalLoader'
import { DropdownOptionString } from 'components/partials/DropdownWithTags'
import { API_TAG } from 'constants/app.constants'
import { ERROR_MESSAGE_1 } from 'constants/content.constants'
import PDSBox from 'designSystem/components/PDSBox/PDSBox'
import PDSButton from 'designSystem/components/PDSButton/PDSButton'
import PDSDropdown from 'designSystem/components/PDSDropdown/PDSDropdown'
import PDSFlexBox from 'designSystem/components/PDSFlexBox/PDSFlexBox'
import PDSSeparator from 'designSystem/components/PDSSeparator/PDSSeparator'
import PDSText from 'designSystem/components/PDSText/PDSText'
import PDSTextInput from 'designSystem/components/PDSTextInput/PDSTextInput'
import { COLOR } from 'designSystem/foundations/constants/color'
import { FONT_SIZE } from 'designSystem/foundations/constants/fontSize'
import { SIZE } from 'designSystem/foundations/constants/size'
import { useCustomToast } from 'hooks/useCustomToast'
import { useAppSelector } from 'store/storeHooks'
import { old_SPACE } from 'styles/constants/old_space'
import {
  getIsConnectedAccount,
  getPortfolioLinkType,
  shouldLockPortfolio,
} from 'utils/portfolioUtil'

import BrokerIcon from '../../../Shared/BrokerIcon'

type PortfolioSettingsForm = {
  displayName: string | undefined
  clientAccountId: string | undefined
}

type Props = {
  closeModal: () => void
  portfolio: Portfolio
  showDeletePortfolioModal: () => void
  showModal: boolean
}

function EditPortfolioSettingsModal({
  closeModal,
  portfolio,
  showDeletePortfolioModal,
  showModal,
}: Props) {
  const {
    showErrorToast,
    showSuccessToast,
    showInfoToast,
    showViewOnlyDefaultToast,
  } = useCustomToast()

  const [patchPortfolio, { isLoading: isPatchLoading }] =
    paveApi.usePatchPortfolioMutation()

  const { managementOrganizationId } = useAppSelector((state) => state.user)

  const { data: clientAccounts } = paveApi.useGetClientAccountNamesAndIdsQuery(
    {
      managementOrgId: managementOrganizationId as string,
    },
    {
      skip: !managementOrganizationId,
      refetchOnMountOrArgChange: true,
    }
  )

  const isLocked = shouldLockPortfolio(portfolio)

  const clientAccountOptions = clientAccounts?.map((c) => {
    return {
      value: c.id,
      label: c.displayName,
    }
  }) as DropdownOptionString[]

  const {
    displayName: initialDisplayName,
    clientAccountId: initialClientAccountId,
  } = portfolio

  const handleSubmit = (values: PortfolioSettingsForm) => {
    try {
      const {
        displayName: newDisplayName,
        clientAccountId: newClientAccountId,
      } = values
      if (
        newDisplayName !== initialDisplayName ||
        newClientAccountId !== initialClientAccountId
      ) {
        const body = {
          id: portfolio.id,
          displayName: newDisplayName,
          clientAccountId: newClientAccountId,
        }
        patchPortfolio({
          body,
          invalidatesTags: [API_TAG.GROUP],
        })
          .unwrap()
          .then(() => {
            showSuccessToast('Portfolio settings updated!')
            closeModal()
          })
          .catch((e) => {
            const displayMessage = e?.data?.displayMessage
            if (e?.status === HttpStatus.FORBIDDEN) {
              showViewOnlyDefaultToast()
            } else if (displayMessage) {
              showErrorToast(displayMessage)
            } else {
              showErrorToast(
                'An error occurred updating your portfolio settings'
              )
            }
          })
      } else {
        showInfoToast('No changes detected.', 'Please try again.')
      }
    } catch (error) {
      if (
        error !== null &&
        typeof error === 'object' &&
        'status' in error &&
        (error as { status: number }).status === HttpStatus.FORBIDDEN
      ) {
        showViewOnlyDefaultToast()
      } else {
        showErrorToast(ERROR_MESSAGE_1)
      }
    }
  }

  const linkType = getPortfolioLinkType(portfolio)
  const isConnectedAccount = getIsConnectedAccount(linkType)
  const accountNumber = isConnectedAccount
    ? portfolio.brokerageAccounts[0]?.accountInfo?.accountNumber
    : ''

  return (
    <Modal isCentered isOpen={showModal} onClose={closeModal} size="sm">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <PDSBox>
            <PDSText
              color={COLOR.textPrimary}
              fontSize={FONT_SIZE.sm}
              fontWeight={600}
            >
              Portfolio Settings
            </PDSText>
          </PDSBox>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody paddingX={old_SPACE.sp24} paddingBottom={old_SPACE.sp24}>
          <Formik<PortfolioSettingsForm>
            initialValues={{
              displayName: portfolio.displayName,
              clientAccountId: portfolio.clientAccountId,
            }}
            onSubmit={async (values, { setSubmitting }) => {
              await handleSubmit(values)
              setSubmitting(false)
            }}
          >
            {({ isSubmitting, setFieldValue, values }) => (
              <Form>
                <PDSFlexBox flexDirection="column" gap={SIZE.s16}>
                  <PDSTextInput
                    label="Portfolio Name"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFieldValue('displayName', e.target.value)
                    }
                    testId="display-name-field"
                    value={values.displayName}
                  />

                  <PDSDropdown
                    label="Group"
                    placeholder="Select"
                    options={clientAccountOptions}
                    onChange={(option: unknown) => {
                      const { value } = option as { value: string }
                      setFieldValue('clientAccountId', value)
                    }}
                    testId="client-account-dropdown"
                    value={values.clientAccountId}
                  />

                  <PDSBox>
                    <PDSFlexBox alignItems="center" height="2rem">
                      <PDSText color={COLOR.textSecondary} weight="medium">
                        Brokerage Account
                      </PDSText>
                    </PDSFlexBox>
                    <PDSFlexBox
                      alignItems="center"
                      border={`1px solid ${COLOR.grey400}`}
                      borderRadius={SIZE.s4}
                      gap={SIZE.s12}
                      height="4rem"
                      paddingLeft={SIZE.s8}
                    >
                      <BrokerIcon linkType={linkType} />
                      <PDSBox>
                        <PDSBox>
                          <PDSText weight="medium">{linkType}</PDSText>
                        </PDSBox>
                        {accountNumber && (
                          <PDSBox>
                            <PDSText>{accountNumber}</PDSText>
                          </PDSBox>
                        )}
                      </PDSBox>
                    </PDSFlexBox>
                  </PDSBox>

                  <PDSBox paddingTop={SIZE.s8}>
                    <PDSButton
                      disabled={isLocked}
                      text="Delete account"
                      variant="danger"
                      onClick={(e: React.SyntheticEvent) => {
                        e.preventDefault()
                        showDeletePortfolioModal()
                      }}
                      size="sm"
                    />
                  </PDSBox>

                  <PDSFlexBox
                    flexDirection="column"
                    gap={SIZE.s4}
                    marginTop={SIZE.s4}
                  >
                    <PDSSeparator marginBottom={SIZE.s16} />
                    <PDSButton
                      text="Save"
                      type="submit"
                      disabled={
                        isPatchLoading ||
                        isSubmitting ||
                        !values.displayName ||
                        !values.clientAccountId
                      }
                    />
                    <PDSButton
                      text="Cancel"
                      type="button"
                      variant="tertiary"
                      onClick={closeModal}
                    />
                  </PDSFlexBox>
                </PDSFlexBox>
              </Form>
            )}
          </Formik>
        </ModalBody>
        {isPatchLoading && <ModalLoader />}
      </ModalContent>
    </Modal>
  )
}

export default EditPortfolioSettingsModal
